import React, { useEffect, useState } from 'react';
import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    CircularProgress,
    TextField,
    Box
} from '@mui/material';

interface LocalityDropdownProps {
    selectedState: string | null;
    selectedGpci: string | null;
    gpciLocalityData: Array<{state: string, gpci: string, locality: string}>;
    value: string | null;
    onChange: (value: string) => void;
    onBlur?: () => void;
    error?: string;
    disabled?: boolean;
    required?: boolean;
}

const LocalityDropdown: React.FC<LocalityDropdownProps> = ({
    selectedState,
    selectedGpci,
    gpciLocalityData,
    value,
    onChange,
    onBlur,
    error,
    disabled = false,
    required = false
}) => {
    const [searchTerm, setSearchTerm] = useState('');

    // Filter localities from gpciLocalityData based on selected state and GPCI
    const localities = React.useMemo(() => {
        if (!selectedState || !selectedGpci || !gpciLocalityData.length) {
            return [];
        }

        // Filter by state and GPCI, then get unique localities
        const filtered = gpciLocalityData
            .filter(item => 
                item.state.toUpperCase() === selectedState.toUpperCase() &&
                item.gpci === selectedGpci
            )
            .map(item => ({
                code: item.locality,
                name: `Locality ${item.locality}` // You can enhance this with actual locality names if available
            }));

        // Remove duplicates
        const unique = Array.from(new Map(filtered.map(item => [item.code, item])).values());
        return unique;
    }, [selectedState, selectedGpci, gpciLocalityData]);

    // Filter localities based on search term (client-side filtering)
    const filteredLocalities = searchTerm.trim()
        ? localities.filter(locality => 
            locality.code.toLowerCase().includes(searchTerm.trim().toLowerCase()) ||
            locality.name.toLowerCase().includes(searchTerm.trim().toLowerCase())
          )
        : localities;

    const isDisabled = disabled || !selectedState || !selectedGpci;

    const getPlaceholderText = () => {
        if (!selectedState) return 'Select State First';
        if (!selectedGpci) return 'Select GPCI First';
        return 'Select Locality';
    };

    return (
        <FormControl fullWidth error={!!error} disabled={isDisabled} required={required}>
            <InputLabel id="locality-select-label">Locality</InputLabel>
            <Select
                labelId="locality-select-label"
                id="locality-select"
                value={value || ''}
                label="Locality"
                onChange={(e) => {
                    const newValue = e.target.value;
                    // Only call onChange if value actually changed
                    if (newValue !== value) {
                        onChange(newValue);
                    }
                }}
                onBlur={onBlur}
                onClose={() => {
                    // Clear search when dropdown closes
                    setSearchTerm('');
                }}
                MenuProps={{
                    autoFocus: false,
                    PaperProps: {
                        style: {
                            maxHeight: 400,
                        },
                        onMouseDown: (e) => {
                            // Prevent dropdown from closing when clicking inside
                            e.stopPropagation();
                        }
                    },
                }}
            >
                <Box 
                    sx={{ px: 2, py: 1, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <TextField
                        size="small"
                        fullWidth
                        placeholder="Search localities..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        autoFocus
                    />
                </Box>
                <MenuItem value="" disabled>
                    <em>{getPlaceholderText()}</em>
                </MenuItem>
                {filteredLocalities.length === 0 ? (
                    <MenuItem disabled>
                        <em>{searchTerm ? 'No localities match your search' : 'No localities available'}</em>
                    </MenuItem>
                ) : (
                    filteredLocalities.map((locality) => (
                        <MenuItem key={locality.code} value={locality.code}>
                            {locality.code}
                        </MenuItem>
                    ))
                )}
            </Select>
            {error && <FormHelperText>{error}</FormHelperText>}
        </FormControl>
    );
};

export default LocalityDropdown;
